"use client";

import { useEffect, useRef, useState } from "react";
export const PTR_THRESHOLD = 70;
export const PTR_MAX_PULL = 140;
export const PTR_MIN_REFRESH_MS = 450;
export type PtrPhase = "idle" | "pulling" | "refreshing";
export function applyResistance(dy: number, threshold = PTR_THRESHOLD, max = PTR_MAX_PULL): number {
  if (dy <= 0) return 0;
  const knee = threshold * 0.6;
  if (dy <= knee) return dy;
  const span = Math.max(1, max - knee);
  const excess = dy - knee;
  const tail = span * (1 - Math.exp(-excess / span));
  return Math.min(max, knee + tail);
}
export function pullProgress(offset: number, threshold = PTR_THRESHOLD): number {
  if (offset <= 0) return 0;
  return Math.min(1, offset / threshold);
}
export function shouldTrigger(offset: number, threshold = PTR_THRESHOLD): boolean {
  return offset >= threshold;
}
function isStandaloneDisplayMode(): boolean {
  if (typeof window === "undefined") return false;
  const mq = window.matchMedia?.("(display-mode: standalone)");
  const iosStandalone = (window.navigator as Navigator & {
    standalone?: boolean;
  }).standalone === true;
  return Boolean(mq?.matches) || iosStandalone;
}
const MOBILE_MQ = "(max-width: 768px) and (pointer: coarse)";
function isMobileViewport(): boolean {
  if (typeof window === "undefined") return false;
  return Boolean(window.matchMedia?.(MOBILE_MQ).matches);
}
const SUPPRESS_SELECTOR = ["input", "textarea", "select", "[contenteditable]", "[contenteditable='true']", "[data-ptr-disabled]", "[data-vaul-drawer]", "[data-state='open'][role='dialog']", "[role='alertdialog']"].join(",");
function hasScrolledAncestor(start: Element | null, stop: Element): boolean {
  let node: Element | null = start;
  while (node && node !== stop) {
    const style = window.getComputedStyle(node);
    const overflowY = style.overflowY;
    if ((overflowY === "auto" || overflowY === "scroll") && node.scrollTop > 0) {
      return true;
    }
    node = node.parentElement;
  }
  return false;
}
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}
export interface UsePullToRefreshOptions {
  onRefresh: () => Promise<void>;
  onOffsetChange?: (offset: number, progress: number) => void;
  onPhaseChange?: (phase: PtrPhase) => void;
  onThresholdCross?: () => void;
  getDisabled?: () => boolean;
  minRefreshMs?: number;
  threshold?: number;
  maxPull?: number;
}
export function usePullToRefresh(options: UsePullToRefreshOptions): {
  phase: PtrPhase;
} {
  const {
    onRefresh,
    onOffsetChange,
    onPhaseChange,
    onThresholdCross,
    getDisabled,
    minRefreshMs = PTR_MIN_REFRESH_MS,
    threshold = PTR_THRESHOLD,
    maxPull = PTR_MAX_PULL
  } = options;
  const [phase, setPhase] = useState<PtrPhase>("idle");
  const phaseRef = useRef<PtrPhase>("idle");
  const optionsRef = useRef({
    onRefresh,
    onOffsetChange,
    onPhaseChange,
    onThresholdCross,
    getDisabled
  });
  optionsRef.current = {
    onRefresh,
    onOffsetChange,
    onPhaseChange,
    onThresholdCross,
    getDisabled
  };
  useEffect(() => {
    if (typeof window === "undefined") return;
    const standaloneMq = window.matchMedia("(display-mode: standalone)");
    const mobileMq = window.matchMedia(MOBILE_MQ);
    let detachTouch: (() => void) | null = null;
    const setPhaseBoth = (next: PtrPhase) => {
      phaseRef.current = next;
      setPhase(next);
      optionsRef.current.onPhaseChange?.(next);
    };
    const emitOffset = (offset: number) => {
      optionsRef.current.onOffsetChange?.(offset, pullProgress(offset, threshold));
    };
    const attach = () => {
      if (detachTouch) return;
      let startY = 0;
      let startX = 0;
      let tracking = false;
      let pulling = false;
      let crossedThreshold = false;
      let rafId = 0;
      let pendingOffset = 0;
      const scheduleOffset = (offset_0: number) => {
        pendingOffset = offset_0;
        if (rafId) return;
        rafId = window.requestAnimationFrame(() => {
          rafId = 0;
          emitOffset(pendingOffset);
        });
      };
      const reset = () => {
        tracking = false;
        pulling = false;
        crossedThreshold = false;
        if (rafId) {
          window.cancelAnimationFrame(rafId);
          rafId = 0;
        }
      };
      const cancelToIdle = () => {
        if (phaseRef.current === "refreshing") return;
        reset();
        emitOffset(0);
        if (phaseRef.current !== "idle") setPhaseBoth("idle");
      };
      const onTouchStart = (ev: TouchEvent) => {
        if (phaseRef.current === "refreshing") return;
        if (ev.touches.length !== 1) {
          reset();
          return;
        }
        if (optionsRef.current.getDisabled?.()) return;
        if (!isStandaloneDisplayMode() || !isMobileViewport()) return;
        const target = ev.target as Element | null;
        if (target?.closest(SUPPRESS_SELECTOR)) return;
        const scroller = document.scrollingElement ?? document.documentElement;
        if (scroller.scrollTop > 0) return;
        if (hasScrolledAncestor(target, scroller)) return;
        const startTouch = ev.touches[0];
        if (!startTouch) return;
        startY = startTouch.clientY;
        startX = startTouch.clientX;
        tracking = true;
        pulling = false;
        crossedThreshold = false;
      };
      const onTouchMove = (ev_0: TouchEvent) => {
        if (!tracking || phaseRef.current === "refreshing") return;
        if (ev_0.touches.length !== 1) {
          cancelToIdle();
          return;
        }
        const touch = ev_0.touches[0];
        if (!touch) return;
        const dy = touch.clientY - startY;
        const dx = touch.clientX - startX;
        if (!pulling) {
          if (dy <= 0) {
            tracking = false;
            return;
          }
          if (Math.abs(dx) > Math.abs(dy)) {
            tracking = false;
            return;
          }
          if (dy < 6) return;
          pulling = true;
          setPhaseBoth("pulling");
        }
        if (ev_0.cancelable) ev_0.preventDefault();
        const offset_1 = applyResistance(dy, threshold, maxPull);
        scheduleOffset(offset_1);
        if (!crossedThreshold && shouldTrigger(offset_1, threshold)) {
          crossedThreshold = true;
          optionsRef.current.onThresholdCross?.();
        } else if (crossedThreshold && !shouldTrigger(offset_1, threshold)) {
          crossedThreshold = false;
        }
      };
      const runRefresh = async () => {
        setPhaseBoth("refreshing");
        emitOffset(threshold);
        try {
          await Promise.all([optionsRef.current.onRefresh(), sleep(minRefreshMs)]);
        } catch {
          /* swallow — user already sees retract */
        } finally {
          emitOffset(0);
          setPhaseBoth("idle");
        }
      };
      const onTouchEnd = () => {
        if (!tracking) return;
        const wasPulling = pulling;
        const didCross = crossedThreshold;
        reset();
        if (!wasPulling) return;
        if (didCross) {
          void runRefresh();
        } else {
          emitOffset(0);
          setPhaseBoth("idle");
        }
      };
      const onScroll = () => {
        if (phaseRef.current === "pulling") cancelToIdle();
      };
      const onVisibility = () => {
        if (document.visibilityState !== "visible") cancelToIdle();
      };
      document.addEventListener("touchstart", onTouchStart, {
        passive: true
      });
      document.addEventListener("touchmove", onTouchMove, {
        passive: false
      });
      document.addEventListener("touchend", onTouchEnd, {
        passive: true
      });
      document.addEventListener("touchcancel", onTouchEnd, {
        passive: true
      });
      document.addEventListener("scroll", onScroll, {
        passive: true
      });
      document.addEventListener("visibilitychange", onVisibility);
      detachTouch = () => {
        document.removeEventListener("touchstart", onTouchStart);
        document.removeEventListener("touchmove", onTouchMove);
        document.removeEventListener("touchend", onTouchEnd);
        document.removeEventListener("touchcancel", onTouchEnd);
        document.removeEventListener("scroll", onScroll);
        document.removeEventListener("visibilitychange", onVisibility);
        if (rafId) window.cancelAnimationFrame(rafId);
        detachTouch = null;
      };
    };
    const detach = () => {
      detachTouch?.();
      if (phaseRef.current !== "idle") {
        phaseRef.current = "idle";
        setPhase("idle");
        optionsRef.current.onOffsetChange?.(0, 0);
        optionsRef.current.onPhaseChange?.("idle");
      }
    };
    const sync = () => {
      if (standaloneMq.matches && mobileMq.matches) attach();else detach();
    };
    sync();
    const onMqChange = () => sync();
    standaloneMq.addEventListener?.("change", onMqChange);
    mobileMq.addEventListener?.("change", onMqChange);
    return () => {
      standaloneMq.removeEventListener?.("change", onMqChange);
      mobileMq.removeEventListener?.("change", onMqChange);
      detach();
    };
  }, [threshold, maxPull, minRefreshMs]);
  return {
    phase
  };
}